import { useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import type { DependencyConfig } from './types';

interface ConditionalFieldProps {
  dependsOn: DependencyConfig | undefined;
  control: Control;
  children: React.ReactNode;
}

/**
 * Wraps a field and conditionally renders it based on a parent field's value.
 *
 * - If `dependsOn` is undefined, always renders.
 * - If `dependsOn.values` is empty, renders when the parent has any non-empty value.
 * - If `dependsOn.values` has entries, renders only when the parent's value is in the list.
 */
export function ConditionalField({
  dependsOn,
  control,
  children,
}: ConditionalFieldProps) {
  if (!dependsOn) return <>{children}</>;

  return (
    <ConditionalFieldInner dependsOn={dependsOn} control={control}>
      {children}
    </ConditionalFieldInner>
  );
}

/**
 * Inner component that calls useWatch unconditionally (hooks can't be conditional).
 */
function ConditionalFieldInner({
  dependsOn,
  control,
  children,
}: {
  dependsOn: DependencyConfig;
  control: Control;
  children: React.ReactNode;
}) {
  const parentValue = useWatch({ control, name: dependsOn.field });

  const isVisible =
    dependsOn.values.length === 0
      ? parentValue !== undefined && parentValue !== ''
      : dependsOn.values.includes(parentValue as string);

  if (!isVisible) return null;

  return <>{children}</>;
}
